/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { Component } from "react";
import PropTypes from "prop-types";
import classnames from "classnames/bind";
import IconButton from "../IconButton";
import withTranslation from "../translation";
import { validate } from "../../../helpers/gesture";
import styles from "./index.css";
import stylesDeferred from "./deferred.css";

const cx = classnames.bind(styles);
const ESCAPE_KEY = 27;
const ENTER_KEY = 13;
const keyCode = e => e.keyCode || e.which;

class Dropdown extends Component {
  state = {
    isOpen: false,
    isOpenOrTransitioning: false
  };

  body;

  setBody = el => {
    this.body = el;
  };

  onClick = e => validate(e, this.props.open);

  onMouseEnter = e => validate(e, this.props.open);

  onMouseLeave = e => validate(e, this.props.onClose);

  closeWithMouse = e => {
    e.stopPropagation();
    validate(e, this.props.onClose);
  };

  handleKeyDown = e => {
    if (keyCode(e) === ESCAPE_KEY) {
      this.props.onClose(e);
    }
  };

  handleEnterKeyDown = e => {
    if (keyCode(e) === ENTER_KEY) {
      this.props.onClose(e);
    }
  };

  /**
   * If the Dropdown is conditionally rendered, it may not animate if rendered and opened in the same frame.
   * Using a setTimeout fixes this issue by queuing the enter transition for the next phase.
   */
  componentWillReceiveProps(nextProps) {
    if (!this.props.isOpen && nextProps.isOpen) {
      requestAnimationFrame(() => {
        this.setState({
          isOpenOrTransitioning: true,
          isOpen: true
        });
      });
    }

    if (!nextProps.isOpen) {
      this.setState({
        isOpen: false
      });
    }
  }

  onTransitionEnd = () => {
    const { isOpen, onTransitionEnd } = this.props;

    if (!isOpen) {
      this.body.scrollTop = 0;

      this.setState({
        isOpenOrTransitioning: false
      });

      onTransitionEnd();
    }
  };

  render() {
    const {
      body,
      children,
      footer,
      formatTranslation,
      header,
      isHidden,
      isOpen,
      name
    } = this.props;

    const wrapperClassnames = cx(styles.wrapper, {
      [styles.wrapper__open]: this.state.isOpen,
      [styles.wrapper__hidden]: isHidden
    });

    const isDisplayed = isOpen || this.state.isOpenOrTransitioning;

    const contentClassnames = cx(styles.content, {
      [styles.content__displayed]: isDisplayed
    });

    return (
      <div
        id={`${name}Dropdown`}
        className={wrapperClassnames}
        onClick={this.onClick}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        onKeyDown={this.handleKeyDown}
        role="presentation"
      >
        {children}
        <div
          className={styles.outerWrapper}
          data-testid={`${name.toLowerCase()}-dropdown`}
          id={`${name.toLowerCase()}-dropdown`}
        >
          <div className={styles.innerWrapper}>
            <div
              className={styles.slidedown}
              onTransitionEnd={this.onTransitionEnd}
            >
              <div className={contentClassnames}>
                <div className={styles.header}>
                  {header && React.cloneElement(header, { focusable: isOpen })}
                  <IconButton
                    icon={stylesDeferred.close}
                    onClick={this.closeWithMouse}
                    onKeyDown={this.handleEnterKeyDown}
                    className={styles.close}
                    data-testid={`${name.toLowerCase()}-close-btn`}
                    aria-label={formatTranslation("icon_close")}
                  />
                </div>
                <ul className={styles.body} ref={this.setBody}>
                  {body &&
                    React.cloneElement(body, {
                      focusable: isOpen,
                      queryParams: { nlid: "nav header" }
                    })}
                </ul>
                {footer && (
                  <div className={styles.footer}>
                    {React.cloneElement(footer, { focusable: isOpen })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Dropdown.propTypes = {
  body: PropTypes.element,
  children: PropTypes.node,
  footer: PropTypes.element,
  formatTranslation: PropTypes.func.isRequired,
  header: PropTypes.element,
  isHidden: PropTypes.bool,
  isOpen: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onTransitionEnd: PropTypes.func.isRequired,
  open: PropTypes.func
};

export default withTranslation(Dropdown);
